import React from "react";

import {
  Box,
  Button,
  Flex,
  IconFacebook,
  IconInstagram,
  IconTikTok,
  IconTwitter,
  ImageResponsive,
  Link,
  Text,
} from "@catchoftheday/cg-components";
import { DEFAULT_APP_DOWNLOAD_LINK } from "@catchoftheday/common-vendors";
import { css, minusOnePx, styled, theme, useMedia } from "@catchoftheday/theme";

import { AppDownloadLink } from "../../types";
import { LinkImage } from "../IESupportingImageLink";

function switchSite() {
  return isMobileSite()
    ? location.href.replace("m.catch", "www.catch")
    : location.href.replace("www.catch", "m.catch");
}

function isMobileSite() {
  if (typeof location === "undefined") {
    return null;
  }
  return location.host.includes("m.catch");
}

export const SiteTypeButton = () => {
  return (
    <Button
      variant="plain"
      fontSize="sm"
      textTransform="none"
      color="secondary"
      fontWeight="semibold"
      onClick={() => location.assign(switchSite())}
    >
      {isMobileSite() ? "View Desktop Site ›" : "View Mobile Site ›"}
    </Button>
  );
};

const SocialContainer = styled(Flex)`
  align-items: center;

  & > a {
    margin-left: 6px;
    margin-right: 6px;
    height: 30px;
    width: 30px;
    background: black;
    border-radius: 50%;

    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    text-decoration: none;

    svg {
      color: white;
    }
  }
`;

type IconLinksProps = {
  appDownloadLinks?: AppDownloadLink[];
};

export const IconLinks = ({ appDownloadLinks }: IconLinksProps) => {
  let appStoreLink: AppDownloadLink | undefined,
    googlePlayLink: AppDownloadLink | undefined;
  if (appDownloadLinks && appDownloadLinks.length > 0) {
    appStoreLink = appDownloadLinks.find((link) => link.name === "appStore");
    googlePlayLink = appDownloadLinks.find(
      (link) => link.name === "googlePlay"
    );
  }
  const isMobile = useMedia<boolean>(
    [`(max-width: ${minusOnePx(theme.breakpoints.md, true)})`],
    [true]
  );

  return (
    <Box
      paddingRight="20px"
      paddingLeft="20px"
      display="flex"
      justifyContent="space-between"
      alignItems={"center"}
      flexDirection={{ xs: "column", md: "row" }}
      marginTop={"30px"}
      width={"100%"}
    >
      {isMobile ? (
        <Flex height="60px" alignItems="center">
          <LinkImage
            href={appStoreLink?.url || DEFAULT_APP_DOWNLOAD_LINK}
            title="Download app from App Store"
            src="https://s.catch.com.au/static/catch_mobile/images/sprites/normal/app-icon--ios.png"
            alt="App Store Logo"
            width={"120px"}
            height={"40px"}
            marginRight="20px"
          />
          <LinkImage
            href={googlePlayLink?.url || DEFAULT_APP_DOWNLOAD_LINK}
            title="Download app from Google Play"
            src="https://s.catch.com.au/static/catch_mobile/images/sprites/normal/app-icon--android.png"
            alt="Play Store Logo"
            width={"135px"}
            height={"40px"}
            marginRight="20px"
          />
        </Flex>
      ) : (
        <Flex height="100px" alignItems="center">
          <Box width="100px" height="100px" mr="16px">
            <ImageResponsive
              src="https://s.catch.com.au/assets/0000/0973/60b9ae8faf321792202603.jpg"
              sourceWidths={[100, 200, 300, 400]}
              width="100"
              height="100"
              alt="Catch App QR Code"
              ratio={100}
              lazyLoad
            />
          </Box>
          <Box>
            <Box>
              <Text
                fontWeight="semibold"
                color="secondary"
                textTransform="uppercase"
                mt="0"
                mb="0"
              >
                Even Better on the App
              </Text>
            </Box>
            <Box>Scan this code to download the Catch app!</Box>
          </Box>
        </Flex>
      )}

      <SocialContainer>
        <Link
          css={css`
            &:hover {
              background-color: #39599b;
            }
          `}
          hoverColor="white"
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.facebook.com/CatchAustralia"
          title="Facebook"
        >
          <IconFacebook width={16} height={16} />
        </Link>
        <Link
          css={css`
            &:hover {
              background-color: #1da1f2;
            }
          `}
          hoverColor="white"
          target="_blank"
          rel="noopener noreferrer"
          href="https://twitter.com/catchaustralia"
          title="Twitter"
        >
          <IconTwitter width={16} height={16} />
        </Link>
        <Link
          css={css`
            &:hover {
              background: linear-gradient(-135deg, #1400c8, #b900b4, #f50000);
            }
          `}
          hoverColor="white"
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.instagram.com/catchaustralia"
          title="Instagram"
        >
          <IconInstagram width={16} height={16} />
        </Link>
        <Link
          css={css`
            &:hover {
              background-color: #fe2c55;
            }
          `}
          hoverColor="white"
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.tiktok.com/@catch.com.au"
          title="TikTok"
        >
          <IconTikTok width={16} height={16} />
        </Link>

        <SiteTypeButton />
      </SocialContainer>
    </Box>
  );
};
